import { useState, useRef } from "react";
import { Card } from "../../Components/Card";
import { Circle } from "../../Components/Circle";
import { PrimaryButton } from "../../Components/Button";
import { EyeIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/solid";
import { createContext, useContext, useEffect } from "react";
import { useFetch } from "../../hooks/useFetch";
import primeClient, { primeClient2 } from "../../axios/primeClient";
import { HorizontalProgressBar } from "../../Components/ProgressBar";
import Recommendations from "../../Components/Recommendations";
import { Modal } from "../../Components/Modal";
import snmpOption from "./snmp-option.png";
import ctaProcesses1 from "./CTA-img/processes-1.png";
import ctaProcesses2 from "./CTA-img/processes-2.png";
import ctaProcesses3 from "./CTA-img/processes-3.png";
import ctaContainer from "./CTA-img/container.png";

import { loopRootCause, isRootCauseEmpty, loopRootCauseServer, loopRootCauseID, loopRootCauseServerID } from "../../utils/loopRootCause";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateCircularProgressBarOption,
  PieChart,
} from "../../Components/PieChart";
import * as echarts from "echarts";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "react-tooltip";
import { useTranslation } from "react-i18next";
import { Colors } from "../../constants/Colors";
// import i18n from "../../i18n";

const DataContext = createContext();

const formatCountstoString = (list) => {
  // (1 up, 1 down) or (1 up) or (1 down if no up)
  let result = "";
  for (let i = list.length - 1; i >= 0; i--) {
    if (list[i].count > 0) {
      // insert from the back
      if (result === "") {
        result += `(${list[i].count} ${list[i].status}`;
      } else {
        result += `, ${list[i].count} ${list[i].status}`;
      }
    }
  }
  if (result !== "") {
    result += `)`;
  }
  if(result === "()")
    result = "";
  console.log("result", result);
  return result;
};
  

export const PrimeDeviceDetailAnalysis = ({ networkId }) => {
  const isMobile = window.innerWidth <= 600;
  const { data, isLoading, error } = useFetch({
    url: `/v1/apps/detail?network_id=${networkId}&type=network`,
    client: primeClient,
  });
  const { t, i18n } = useTranslation();
  const [rootCause, setRootCause] = useState([]);
  const [cpuData, setCpuData] = useState([]);
  const [memoryData, setMemoryData] = useState([]);
  const [storageData, setStorageData] = useState([]);

  useEffect(() => {
    if (!isLoading && !error && data) {
      const { cpuData, memoryData, storageData, rootCause } = 
      i18n.language === "id" ? loopRootCauseID(data.device[0]) :
      loopRootCause(
        data.device[0]
      );
      setRootCause(rootCause);
      setCpuData(cpuData);
      setMemoryData(memoryData);
      setStorageData(storageData);
    }
  }, [data, isLoading, error]);

  return (
    <DataContext.Provider value={{ data, isLoading, error, t, i18n, formatCountstoString }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <DeviceDescriptionCard />
        <Card
          style={{
            padding: "1rem",
            width: "100%",
            maxWidth: window.innerWidth - 30,
          }}
        >
        <TrafficUtilizationCard />
        < br />
        < hr />
        < br />
        <div
          style={{
            display: "grid",
            gap: "1.8rem",
            gridTemplateColumns: isMobile
              ? "repeat(auto-fill, minmax(220px, 1fr))"
              : "1fr 1fr",
            minHeight: "7rem",
          }}
        >
          <DeviceCapacityCard
            cpuData={cpuData}
            memoryData={memoryData}
            storageData={storageData}
          />
          

          <NetworkQualityCard networkId={networkId} />
        </div>
        < br /> < hr /> < br />
        {isLoading || error
          ? null
          : data.device[0].device_status && (
            <>
            <CapacityForecastCard networkId={networkId} />
            < br /> < hr /> < br />
            </>
          )}
        
        {isLoading || error ? null
          : data.device[0].device_status ?
            <Recommendations
              rootCause={rootCause}
              deviceName={isLoading || error ? "Device Name" : data.device[0].name}
            /> :
            isRootCauseEmpty(rootCause) ? <Recommendations rootCause={[
              { deviceDown: [`Device ${data.device[0].name} power is off or network is down`] }]}

            />
              : <Recommendations
                rootCause={rootCause}
                deviceName={isLoading || error ? "Device Name" : data.device[0].name}
              />
        }
        </Card>
      </div>
    </DataContext.Provider>
  );
};

export const PrimeServerDetailAnalysis = ({ networkId }) => {
  const isMobile = window.innerWidth <= 600;
  const { data, isLoading, error } = useFetch({
    url: `/v1/apps/detail?network_id=${networkId}&type=server`,
    client: primeClient,
  });
  const { t, i18n } = useTranslation();
  const [rootCause, setRootCause] = useState([]);
  const [cpuData, setCpuData] = useState([]);
  const [cpuUserData, setCpuUserData] = useState([]);
  const [memoryData, setMemoryData] = useState([]);
  const [storageData, setStorageData] = useState([]);
  
  useEffect(() => {
    if (!isLoading && !error && data) {
      const { cpuData, cpuUserData, memoryData, storageData, rootCause } = 
      i18n.language === "id" ? loopRootCauseServerID(data.device[0]) :
      loopRootCauseServer(
        data.device[0]
      );
      setRootCause(rootCause);
      setCpuData(cpuData);
      setCpuUserData(cpuUserData);
      setMemoryData(memoryData);
      setStorageData(storageData);
    }
  }, [data, isLoading, error]);

  return (
    <DataContext.Provider value={{ data, isLoading, error, t, i18n, isMobile, formatCountstoString }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <DeviceDescriptionCard />
        <Card 
          style={{
            padding: "1rem",
            width: "100%",
            maxWidth: window.innerWidth - 30,
          }}
        >
        <div
          style={{
            display: "grid",
            gap: "1rem",
            // gridTemplateColumns: isMobile
            //   ? "repeat(auto-fill, minmax(220px, 1fr))"
            //   : "1fr 1fr",
            minHeight: "7rem",
          }}
        >
          <DeviceCapacityServerCard
            cpuData={cpuData}
            cpuUserData={cpuUserData}
            memoryData={memoryData}
            storageData={storageData}
          />
        </div>
        < br /> < hr /> < br />
        <ProcessServerCard />
        < br /> < hr /> < br />
        <ContainerServerCard />
        < br /> < hr /> < br />
        {isLoading || error
          ? null
          : data.device[0].device_status && (
            <>
            <CapacityForecastCard networkId={networkId} />
            < br /> < hr /> < br />
            </>
          )}
        {isLoading || error ? null
          : data.device[0].device_status ?
            <Recommendations
              rootCause={rootCause}
              deviceName={isLoading || error ? "Device Name" : data.device[0].name}
            /> :
            isRootCauseEmpty(rootCause) ? <Recommendations rootCause={[
              { deviceDown: [`Device ${data.device[0].name} power is off or network is down`] }]}

            />
              : <Recommendations
                rootCause={rootCause}
                deviceName={isLoading || error ? "Device Name" : data.device[0].name}
              />
        }
        </Card>
      </div>
    </DataContext.Provider>
  );
}

export const PrimeWebAPIDetailAnalysis = ({ networkId }) => {
  const type = "webapi";
  const { data, isLoading, error } = useFetch({
    url: `/v1/apps/detail?network_id=${networkId}&type=webapi`,
    client: primeClient,
  });

  const { data: dataToday, loading: isLoadingToday, error: errorToday } = useFetch({
    url: `/v1/apps/detail/component?network_id=${networkId}&type=webapi&duration=day&component=cpu`,
    client: primeClient,
  });

  const {
    data: dataWeek,
    isLoading: isLoadingWeek,
    error: errorWeek,
  } = useFetch({
    url: `/v1/apps/detail/component?network_id=${networkId}&type=webapi&duration=week&component=cpu`,
    client: primeClient,
  });

  console.log("webapi", data);

  const { t, i18n } = useTranslation();

  const [isToday, setIsToday] = useState(true);
  const [isAnomalyClicked, setIsAnomalyClicked] = useState(false);

  const toggleToday = () => {
    setIsToday((prev) => !prev);
  };

  const clickAnomaly = () => {
    setIsAnomalyClicked(true);
  };

  const closeAnomaly = () => {
    setIsAnomalyClicked(false);
  };

  const defineLevel = (value) => {
    if (value < 1) {
      return "normal";
    } else if (value >= 1 && value < 1.2) {
      return "warning";
    } else if (value >= 1.2) {
      return "critical";
    } else {
      return "no_data";
    }
  };

  useEffect(() => {
    closeAnomaly();
  }, []);
  return (
    <DataContext.Provider
      value={{

        data,
        isLoading,
        error,
        dataToday,
        isLoadingToday,
        errorToday,
        dataWeek,
        isLoadingWeek,
        errorWeek,
        isToday,
        t,
        i18n,
        defineLevel
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        {isLoading || error || !data || data.error || isLoadingToday || !dataToday || isLoadingWeek || !dataWeek 
        ? null : (
          <>
        <DeviceDescriptionCard type={"webapi"} />
      
        <Card style={{ padding: "1rem", marginTop: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <p
              style={{
                fontWeight: "bold",
                fontSize: "1.1rem",
                textAlign: "center",
              }}
            >
              Response Time
            </p>

            <Picker isToday={isToday} toggleToday={toggleToday} />
          </div>


          {isToday ? (
            isLoadingToday || !dataToday ? (
              <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                Loading...
              </p>
            ) : errorToday ? (
              <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                {errorToday.message}
              </p>
            )
              : dataToday.error ? (
                <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                  {dataToday.message}
                </p>
              ) :
                (
                  <>
                    <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                      Latest web/API Response Time is {dataToday.usage_condition_category}
                    </p>
                    <PerformanceChart
                      data={dataToday.usage.map((d) => d.latency)}
                      labels={dataToday.usage.map((d) => d.time)}
                    />
                  </>
                )
          )
            : isLoadingWeek || !dataWeek ? (
              <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                Loading...
              </p>
            ) : errorWeek ? (
              <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                {errorWeek.message}
              </p>
            )
              : dataWeek.error ? (
                <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                  {dataWeek.message}
                </p>
              ) : (
                <>
                  <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                    Latest web/API Response Time is {dataWeek.usage_condition_category}
                  </p>
                  <PerformanceChart
                    data={dataWeek.usage.map((d) => d.latency)}
                    labels={dataWeek.usage.map((d) => d.time)}

                  />
                </>
              )}


        
        
        <TrendTableCard clickAnomaly={clickAnomaly} type={type} />
        < br /> < hr /> < br />
        <SummaryAnalysisCard type={type} />

        {isToday ? (
          !isLoading  && data && dataToday && dataToday.usage_condition_category && dataToday.usage_condition_category === "normal" ?
            null :
            !dataToday.error &&
            <>
            < br /> < hr /> < br /> 

            <Recommendations rootCause={[
              { webapi: [`Web/API Response Time on ${data.device[0].name} is ${dataToday.usage_condition_category}`] }]}
            />
            </>
        ) :
          !isLoading && data && dataWeek && dataWeek.usage_condition_category  && dataWeek.usage_condition_category === "normal" ?
            null :
            !dataWeek.error &&
            <>
            < br /> < hr /> < br />
            <Recommendations rootCause={[
              { webapi: [`Web/API Response Time on ${data.device[0].name} is ${dataWeek.usage_condition_category}`] }]}
            />
            </>
        }
        </Card>

        </>
      
        )}


      </div>
      {isAnomalyClicked && (
        <FloatingAnomalies type={type} closeAnomaly={closeAnomaly} />
      )}
    </DataContext.Provider>

  );

};

// prettier-ignore
const statusColors = {
  "UP": Colors.alertsgreen100,
  "up": Colors.alertsgreen100,
  "DOWN": Colors.alertsred100,
  "down": Colors.alertsred100,
}

const DeviceDescriptionCard = ({ type }) => {
  const { data, isLoading, error, t } = useContext(DataContext);
  console.log("data", data);

  return (
    <Card
      style={{
        width: "100%",
        maxWidth: "400px",
        backgroundColor: Colors.primary100,
        color: Colors.white,
        padding: "0.5rem 1rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "bold",
        }}
      >
        <div>{isLoading || error || !data || data.error ? "Device Name" : data.device[0].name}</div>
        {!isLoading && !error && data && !data.error && data.device.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Circle
              color={statusColors[data.device[0].device_status ? "UP" : "DOWN"]}
              diameter={"0.5rem"}
            />
            {data.device[0].device_status ? "UP" : "DOWN"}
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>

          {isLoading || error || !data || data.error ? "xxx.xxx.xxx.xxx" :
            type === "webapi" ? data.device[0].url : data.device[0].ip_address}
        </div>
        <div>{!isLoading && !error && data.device[0].last_update}</div>
      </div>
      {type === "webapi" ? (
        !isLoading && !error && data.device[0].device_status === false && (
          <div
            style={{
              fontWeight: 500,
              fontSize: "0.8rem",
              marginTop: "0.5rem",
            }}
          >
            {t("prime.detailAnalysis.lastCondition", { time: "23-10-2021 12:00" })}
          </div>
        )
      ) : (
        <></>
      )}
    </Card>
  );
};

const TrafficUtilizationCard = () => {
  const { data, isLoading, error, t, i18n } = useContext(DataContext);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [trafficUtilizationData, setTrafficUtilizationData] = useState(null);

  useEffect(() => {
    if (data) {
      setTrafficUtilizationData(
        data.device[0].traffic.find((el) => el.type === "link_utilization")
      );
    }
  }, [data]);
  const toggleModal = () => setIsModalOpen((prev) => !prev);

  const isEmpty = (utilizationData) => {
    return !utilizationData || utilizationData.length === 0;
  };

  return (
    <div
      style={{
        // padding: "0.5rem 1rem",
        width: "100%",
        maxWidth: window.innerWidth - 30,
      }}
    >
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <div style={{ paddingLeft: "1rem" }}>
          <h2 style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
            {i18n.language === "id"
              ? "Traffic Utilization Belum Termonitor"
              : "Traffic Utilization Not Monitored Yet"}
          </h2>
          <p>
            {i18n.language === "id" ? (
              <>
                Silahkan buka halaman Link Utilization di Netmonk Prime,
                <br />
                pilih interface yang ingin dimonitor, lalu klik
                <br />
                “Start Monitoring” untuk memulai pemantauan.
              </>
            ) : (
              <>
                Please open the Link Utilization page in Netmonk Prime,
                <br />
                select the interface you want to monitor, then click
                <br />
                “Start Monitoring” to start monitoring.
              </>
            )}
          </p>
        </div>
      </Modal>
      <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
        {t("prime.detailAnalysis.trafficUtilization.header")}
        {isLoading || error || data.device === null || (data?.device[0].count_traffic.up === 0 && data?.device[0].count_traffic.down === 0) ? null :
          ` (${data?.device[0].count_traffic.up} Up, ${data?.device[0].count_traffic.down} Down)`
        }
      </div>
      <div
        style={{
          // padding: "0.5rem",
          overflowX: "auto",
        }}
      >
        <table style={{ width: "100%" }}>
          <thead>
            <tr
              style={{
                textAlign: "center",
                fontSize: "0.8rem",
                fontWeight: 500,

              }}
            >
              <th
                style={{
                  backgroundColor: Colors.neutral10,
                  borderTopLeftRadius: "5px",
                  borderBottomLeftRadius: "5px",
                  padding: "0.5rem",
                }}
              >
                {t("prime.detailAnalysis.trafficUtilization.status")}
              </th>
              <th style={{ backgroundColor: Colors.neutral10, padding: "0.5rem", }}>
                {t("prime.detailAnalysis.trafficUtilization.interface")}
              </th>
              <th style={{ backgroundColor: Colors.neutral10, padding: "0.5rem", }}>
                {t("prime.detailAnalysis.trafficUtilization.in")} (%)
              </th>
              <th style={{ backgroundColor: Colors.neutral10, padding: "0.5rem", }}>
                {t("prime.detailAnalysis.trafficUtilization.out")} (%)
              </th>
              <th style={{ backgroundColor: Colors.neutral10, padding: "0.5rem", }}>
                {t("prime.detailAnalysis.trafficUtilization.inSpeed")}
              </th>
              <th style={{ backgroundColor: Colors.neutral10, padding: "0.5rem", }}>
                {t("prime.detailAnalysis.trafficUtilization.outSpeed")}
              </th>
              <th
                style={{
                  backgroundColor: Colors.neutral10,
                  padding: "0.5rem",
                }}
              >
                {t("prime.detailAnalysis.trafficUtilization.bandwidth")}
              </th>
              <th
                style={{
                  backgroundColor: Colors.neutral10,
                  borderTopRightRadius: "5px",
                  borderBottomRightRadius: "5px",
                  padding: "0.5rem",
                }}
              >
                {t("prime.detailAnalysis.trafficUtilization.detail")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading || error || isEmpty(trafficUtilizationData)
              ? null
              : trafficUtilizationData.interface.map((el, idx) => {
                return (
                  <tr
                    key={`traffic-${idx}`}
                    style={{
                      textAlign: "center",
                      height: "1.5rem",
                      borderBottom: `1px solid ${Colors.neutral20}`,
                      fontSize: "0.8rem",
                    }}
                  >
                    <td
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "1.5rem",
                        margin: "3px 0",
                      }}
                    >
                      {el.status === "no_data" ? (
                        "N/A"
                      ) : (
                        <Circle
                          color={statusColors[el.status]}
                          diameter={"1rem"}
                        />
                      )}
                    </td>
                    <td>{el.interface}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "5px",
                        }}
                      >
                        <HorizontalProgressBar
                          percentage={el.in_percentage}
                          width="5rem"
                          height="1rem"
                          dataColor="#fb3640"
                        />
                        {el.status === "no_data"
                          ? "N/A"
                          : `${Math.max(el.in_percentage, 0)}%`}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "5px",
                        }}
                      >
                        <HorizontalProgressBar
                          percentage={el.out_percentage}
                          width="5rem"
                          height="1rem"
                          dataColor="#ffb220"
                        />
                        {el.status === "no_data"
                          ? "N/A"
                          : `${Math.max(el.out_percentage, 0)}%`}
                      </div>
                    </td>
                    <td>
                      {el.status === "no_data"
                        ? "N/A"
                        : `${el.in_value} ${el.in_metric}`}
                    </td>
                    <td>
                      {el.status === "no_data"
                        ? "N/A"
                        : `${el.out_value} ${el.out_metric}`}
                    </td>
                    <td>
                      {el.status === "no_data"
                        ? "N/A"
                        : `${el.max_capacity} ${el.max_capacity_metric}`}
                    </td>
                    <td
                      style={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {el.status === "no_data" ? (
                        <QuestionMarkCircleIcon
                          width={"1rem"}
                          style={{ display: "inline", cursor: "pointer" }}
                          onClick={toggleModal}
                        />
                      ) : (
                        <PrimaryButton
                          icon={<EyeIcon width={"1rem"} />}
                          style={{
                            transform: "scale(0.7)",
                            padding: "10px",
                          }}
                          onClick={() =>
                            navigate("traffic-detail/" + el.interface_id)
                          }
                        />
                      )}
                    </td>
                  </tr>
                );
              }) ?? null}
          </tbody>
        </table>
        {!isLoading && !error && isEmpty(trafficUtilizationData) && (
          <div
            style={{
              width: "100%",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "1rem",
              paddingTop: "0.3rem",
              fontSize: "0.8rem",
              height: "3rem",
            }}
          >
            {i18n.language === "id" ? "Data tidak tersedia " : "Data not available "}
          
            <QuestionMarkCircleIcon
              width={"1rem"}
              style={{ display: "inline", cursor: "pointer" }}
              onClick={toggleModal}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const DeviceCapacityCard = ({ cpuData, memoryData, storageData }) => {
  const [cpuCharts, setCpuCharts] = useState([]);
  const [memoryCharts, setMemoryCharts] = useState([]);
  const [storageCharts, setStorageCharts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen((prev) => !prev);
  const navigate = useNavigate();
  const { isMobile } = useContext(DataContext);

  const levelColors = {
    normal: Colors.alertsgreen100,
    poor: Colors.alertsred100,
    critical: Colors.alertsred100,
    warning: Colors.alertsyellow100,
  };

  useEffect(() => {
    const cpuCharts = cpuData.map((el) => {
      return {
        chart: generateCircularProgressBarOption(
          el.level === "no_data" ? "N/A" : el.value + "%",
          null,
          levelColors[el.level],
          el.level === "no_data" ? 100 : el.value
        ),
        ...el,
      };
    });
    setCpuCharts(cpuCharts);

    const memoryCharts = memoryData.map((el) => {
      return {
        chart: generateCircularProgressBarOption(
          el.level === "no_data" ? "no data" : el.value + "%",
          null,
          levelColors[el.level],
          el.level === "no_data" ? 100 : el.value
        ),
        ...el,
      };
    });
    setMemoryCharts(memoryCharts);

    const storageCharts = storageData.map((el) => {
      return {
        chart: generateCircularProgressBarOption(
          el.level === "no_data" ? "no data" : el.value + "%",
          null,
          levelColors[el.level],
          el.level === "no_data" ? 100 : el.value
        ),
        ...el,
      };
    });
    setStorageCharts(storageCharts);
  }, [cpuData, memoryData, storageData]);

  const { i18n, t, data, isLoading, error, formatCountstoString } = useContext(DataContext);

  return (

    <div
      style={{
        padding: "0.5rem 0rem",

      }}
    >
      
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <div style={{ paddingLeft: "1rem" }}>
          <h2 style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
            {i18n.language === "id"
              ? "Device Capacity Belum Termonitor"
              : "Device Capacity Not Monitored Yet"}
          </h2>
          <ol style={{ width: "30rem", listStyleType: "decimal" }}>
            <li>
              {i18n.language === "id"
                ? "Pastikan Anda sudah melakukan ceklis untuk opsi SNMP ketika menambahkan perangkat"
                : "Make sure you have checked the SNMP option when adding the device"}
            </li>
            <li>
              {i18n.language === "id"
                ? "Aktifkan SNMP pada perangkat Anda untuk monitoring perangkat jaringan. Klik "
                : "Activate SNMP on your device for network device monitoring. Click "}
              <a
                style={{ all: "revert" }}
                href="https://netmonk.id/help/articles/%5Bgs-panduan-9%5Dbagaimana-cara-setup-snmp"
              >
                {i18n.language === "id" ? "link berikut" : "the following link"}
              </a>

              {i18n.language === "id"
                ? " untuk melakukan setup SNMP pada perangkat Anda"
                : " to set up SNMP on your device"}
              <img src={snmpOption} />
            </li>
            <li>
              {i18n.language === "id"
                ? "Jika Anda masih mengalami masalah, "
                : "If you are still experiencing problems, "}
              <a
                style={{ all: "revert" }}
                href="https://api.whatsapp.com/send/?phone=628111720237&text&type=phone_number&app_absent=0"
              >
                {i18n.language === "id" ? "hubungi kami" : "contact us"}
              </a>
              .
            </li>
          </ol>
        </div>
      </Modal>
      <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
        {t("prime.detailAnalysis.deviceCapacityandQoS.header")}
        {' '}
        {isLoading || error || data.device === null ? null :
          formatCountstoString([
            { count: data?.device[0].count_component.normal, status: "🟢" },
            { count: data?.device[0].count_component.warning, status: "🟠" },
            { count: data?.device[0].count_component.critical, status: "🔴" },
          ])

        }
      </div>

      {cpuData.length === 0 &&
        memoryData.length === 0 &&
        storageData.length === 0 ? (
        <div
          style={{
            width: "100%",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
            paddingTop: "0.3rem",
            fontSize: "0.8rem",
          }}
        >
          {i18n.language === "id"
            ? "Data tidak tersedia "
            : "Data not available "}
          <QuestionMarkCircleIcon
            width={"1rem"}
            style={{ display: "inline", cursor: "pointer" }}
            onClick={toggleModal}
          />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "0.5rem",
          }}
        >
          <div
            style={{
              minWidth: 0,
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: Colors.neutral10,
                borderTopLeftRadius: "5px",
                marginBottom: "5px",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.cpu")}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                overflowX: "scroll",
                backgroundColor: Colors.neutral2,
                borderBottomLeftRadius: "5px",
                height: "6.5rem",
              }}
            >
              {/* Variable content inside child 1 */}
              {cpuCharts.map((el, idx) => (
                <div key={`cpu-${idx}`}>
                  <PieChart
                    options={el.chart}
                    chartWidth={60}
                    chartHeight={60}
                    onClick={() => navigate("component-detail/cpu/" + el.id)}
                  />
                  <div style={{ textAlign: "center", fontSize: "0.8rem" }}>
                    {idx}{" "}
                    <InformationCircleIcon
                      color="darkgrey"
                      width={"1rem"}
                      style={{
                        display: "inline",
                        cursor: "pointer",
                      }}
                      data-tooltip-id={`cpu-${idx}-tooltip`}
                    />
                    <Tooltip
                      id={`cpu-${idx}-tooltip`}
                      place="bottom"
                      style={{
                        fontSize: "0.7rem",
                        maxWidth: "200px",
                      }}
                      clickable
                    >
                      <div style={{ overflow: "auto" }}>{el.type}</div>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              minWidth: 0,
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: Colors.neutral10,
                marginBottom: "5px",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.memory")}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                overflowX: "scroll",
                backgroundColor: Colors.neutral2,
                height: "6.5rem",
              }}
            >
              {memoryCharts.map((el, idx) => (
                <div key={`memory-${idx}`}>
                  <PieChart
                    options={el.chart}
                    chartWidth={60}
                    chartHeight={60}
                    onClick={() => navigate("component-detail/memory/" + el.id)}
                  />
                  <div style={{ textAlign: "center", fontSize: "0.8rem" }}>
                    {idx}{" "}
                    <InformationCircleIcon
                      color="darkgrey"
                      width={"1rem"}
                      style={{
                        display: "inline",
                        cursor: "pointer",
                      }}
                      data-tooltip-id={`memory-${idx}-tooltip`}
                    />
                    <Tooltip
                      id={`memory-${idx}-tooltip`}
                      place="bottom"
                      style={{
                        fontSize: "0.7rem",
                        maxWidth: "200px",
                      }}
                      clickable
                    >
                      <div style={{ overflow: "auto" }}>{el.type}</div>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              minWidth: 0,
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: Colors.neutral10,
                borderTopRightRadius: "5px",
                marginBottom: "5px",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.storage")}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                overflowX: "scroll",
                backgroundColor: Colors.neutral2,
                borderBottomRightRadius: "5px",
                height: "6.5rem",
              }}
            >
              {storageCharts.map((el, idx) => (
                <div key={`storage-${idx}`}>
                  <PieChart
                    options={el.chart}
                    chartWidth={60}
                    chartHeight={60}
                    onClick={() =>
                      navigate("component-detail/storage/" + el.id)
                    }
                  />
                  <div style={{ textAlign: "center", fontSize: "0.8rem" }}>
                    {idx}{" "}
                    <InformationCircleIcon
                      color="darkgrey"
                      width={"1rem"}
                      style={{
                        display: "inline",
                        cursor: "pointer",
                      }}
                      data-tooltip-id={`storage-${idx}-tooltip`}
                    />
                    <Tooltip
                      id={`storage-${idx}-tooltip`}
                      place="bottom"
                      style={{
                        fontSize: "0.7rem",
                        maxWidth: "200px",
                      }}
                      clickable
                    >
                      <div style={{ overflow: "auto" }}>{el.type}</div>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NetworkQualityCard = ({ networkId }) => {
  const { data, isLoading, error, i18n, t } = useContext(DataContext);
  const [latencyChart, setLatencyChart] = useState(null);
  const [jitterChart, setJitterChart] = useState(null);
  const [packetLossChart, setPacketLossChart] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen((prev) => !prev);
  const navigate = useNavigate();

  const levelColor = {
    normal: Colors.alertsgreen100,
    poor: Colors.alertsyellow100,
    critical: Colors.alertsred100,
  };

  useEffect(() => {
    if (data) {
      const latencyData = data.device[0].network.find(
        (el) => el.type === "latency"
      );
      if (latencyData) {
        const latencyChart = generateCircularProgressBarOption(
          latencyData.value + " ms",
          null,
          levelColor[latencyData.level],
          latencyData.value
        );
        setLatencyChart(latencyChart);
      }

      const jitterData = data.device[0].network.find(
        (el) => el.type === "jitter"
      );
      if (jitterData) {
        const jitterChart = generateCircularProgressBarOption(
          jitterData.value + " ms",
          null,
          levelColor[jitterData.level],
          jitterData.value
        );
        setJitterChart(jitterChart);
      }

      const packetLossData = data.device[0].network.find(
        (el) => el.type === "packet_loss"
      );

      if (packetLossData) {
        const packetLossChart = generateCircularProgressBarOption(
          packetLossData.value + " %",
          null,
          levelColor[packetLossData.level],
          packetLossData.value
        );
        setPacketLossChart(packetLossChart);
      }
    }
  }, [data]);

  return (
    <div
      style={{
        padding: "0.5rem 0rem",
      }}
    >
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <div style={{ paddingLeft: "1rem" }}>
          <h2 style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
            {i18n.language === "id"
              ? "Network Quality Belum Termonitor"
              : "Network Quality Not Monitored Yet"}
          </h2>
          <ol style={{ width: "30rem", listStyleType: "decimal" }}>
            <li>
              {i18n.language === "id"
                ? "Pastikan Anda sudah melakukan ceklis untuk opsi ICMP ketika menambahkan perangkat"
                : "Make sure you have checked the ICMP option when adding the device"}

              <img src={snmpOption} />
            </li>
            <li>
              {i18n.language === "id"
                ? "Jika Anda masih mengalami masalah, "
                : "If you are still experiencing problems, "}
              <a
                style={{ all: "revert" }}
                href="https://api.whatsapp.com/send/?phone=628111720237&text&type=phone_number&app_absent=0"
              >
                {i18n.language === "id" ? "hubungi kami" : "contact us"}
              </a>
              .
            </li>
          </ol>
        </div>
      </Modal>
      <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
        {t("prime.detailAnalysis.deviceCapacityandQoS.networkQuality")}
      </div>
      {!isLoading &&
        !error &&
        !data.device[0].network.find((el) => el.type === "latency") &&
        !data.device[0].network.find((el) => el.type === "jitter") &&
        !data.device[0].network.find((el) => el.type === "packet_loss") ? (
        <div
          style={{
            width: "100%",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
            paddingTop: "0.3rem",
            fontSize: "0.8rem",
          }}
        >
          {i18n.language === "id"
            ? "Data tidak tersedia"
            : "Data not available"}{" "}
          <QuestionMarkCircleIcon
            width={"1rem"}
            style={{ display: "inline", cursor: "pointer" }}
            onClick={toggleModal}
          />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "0.5rem",
          }}
        >
          <div
            style={{
              minWidth: 0,
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
                backgroundColor: Colors.neutral10,
                borderTopLeftRadius: "5px",
                marginBottom: "5px",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.latency")}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "center",
                backgroundColor: Colors.neutral2,
                height: "6.5rem",
                borderBottomLeftRadius: "5px",
              }}
            >
              {latencyChart && (
                <PieChart
                  options={latencyChart}
                  chartWidth={85}
                  chartHeight={85}
                  onClick={() => navigate("qos-detail/latency_avg")}
                />
              )}
            </div>
          </div>

          <div
            style={{
              minWidth: 0,
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: Colors.neutral10,
                // borderRadius: "5px",
                marginBottom: "5px",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.jitter")}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "center",
                backgroundColor: Colors.neutral2,
                height: "6.5rem",

              }}
            >
              {jitterChart && (
                <PieChart
                  options={jitterChart}
                  chartWidth={85}
                  chartHeight={85}
                  onClick={() => navigate("qos-detail/jitter")}
                />
              )}
            </div>
          </div>

          <div
            style={{
              minWidth: 0,
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: Colors.neutral10,
                borderTopRightRadius: "5px",
                marginBottom: "5px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.packetLoss")}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "center",
                backgroundColor: Colors.neutral2,
                height: "6.5rem",
                borderBottomRightRadius: "5px",
              }}
            >
              {packetLossChart && (
                <PieChart
                  options={packetLossChart}
                  chartWidth={85}
                  chartHeight={85}
                  onClick={() => navigate("qos-detail/packet_loss")}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CapacityForecastCard = ({ networkId }) => {
  const { data, isLoading, error } = useFetch({
    url: `/device_condition/list/resources?id=${networkId}`,
    client: primeClient2,
  });
  const navigate = useNavigate();

  useEffect(() => {
    console.log("Data received 1:", data);
    if (data && !isLoading && !error) {
      console.log("Data received:", data);
    } else {
      console.log("error", error);
      console.log("error", error);
    }
  }, [data, isLoading, error]);

  function mapForecastData(dataForecast) {
    let temp = [];
    if (!dataForecast) {
      return temp;
    }
    if (dataForecast.data) {
      for (let i = 0; i < dataForecast.data.cpu.length; i++) {
        let tempData = {
          resource: `CPU`,
          id: dataForecast.data.cpu[i].id,
          index: dataForecast.data.cpu[i].index,
          critical: dataForecast.data.cpu[i].critical_date,
          atCapacity: dataForecast.data.cpu[i].at_capacity_date,
        };
        temp.push(tempData);
      }

      for (let i = 0; i < dataForecast.data.memory.length; i++) {
        let tempData = {
          resource: `Memory`,
          id: dataForecast.data.memory[i].id,
          index: dataForecast.data.memory[i].index,
          critical: dataForecast.data.memory[i].critical_date,
          atCapacity: dataForecast.data.memory[i].at_capacity_date,
        };
        temp.push(tempData);
      }

      for (let i = 0; i < dataForecast.data.storage.length; i++) {
        let tempData = {
          resource: `Storage`,
          id: dataForecast.data.storage[i].id,
          index: dataForecast.data.storage[i].index,
          critical: dataForecast.data.storage[i].critical_date,
          atCapacity: dataForecast.data.storage[i].at_capacity_date,
        };
        temp.push(tempData);
      }
    }
    return temp;
  }

  const { t, i18n } = useContext(DataContext);

  return (
    <div
      style={{
        padding: "0.5rem 0rem",
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
        {t("prime.detailAnalysis.capacityForecast.header")}
      </div>
      {!data || isLoading || error || data.success === false ? (
        <div>{t("prime.detailAnalysis.capacityForecast.normalCondition")}</div>
      ) : (
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th
                style={{
                  backgroundColor: Colors.neutral20,
                  borderTopLeftRadius: "5px",
                  borderBottomLeftRadius: "5px",
                }}
              >
                Resource
              </th>
              <th style={{ backgroundColor: Colors.neutral20 }}>Index</th>
              <th style={{ backgroundColor: Colors.neutral20 }}>
                {i18n.language === "id" ? "Tanggal Kritis" : "Critical Date"}
              </th>
              <th style={{ backgroundColor: Colors.neutral20 }}>
                {i18n.language === "id" ? "Tanggal Penuh" : "At Capacity Date"}
              </th>
              <th
                style={{
                  backgroundColor: Colors.neutral20,
                  borderTopRightRadius: "5px",
                  borderBottomRightRadius: "5px",
                }}
              >
                {i18n.language === "id" ? "Aksi" : "Action"}
              </th>
            </tr>
          </thead>
          <tbody>
            {mapForecastData(data).map((forecastData, index) => (
              <tr
                style={{
                  textAlign: "center",
                  height: "1.5rem",
                  borderBottom: "1px solid #bfbfbf",
                }}
              >
                <td>{forecastData.resource}</td>
                <td>{forecastData.index}</td>
                <td>{forecastData.critical.replace("T", " ").slice(0, 16)}</td>
                <td>
                  {forecastData.atCapacity ? forecastData.atCapacity : "-"}
                </td>
                <td
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <PrimaryButton
                    icon={<EyeIcon width={"1rem"} />}
                    style={{
                      transform: "scale(0.7)",
                      padding: "10px",
                    }}
                    onClick={() =>
                      navigate(
                        "capacity-forecast/" +
                        forecastData.resource +
                        "/" +
                        forecastData.id
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};


const DeviceCapacityServerCard = ({ cpuData, cpuUserData, memoryData, storageData }) => {
  const [cpuCharts, setCpuCharts] = useState([]);
  const [cpuUserCharts, setCpuUserCharts] = useState([]);
  const [memoryCharts, setMemoryCharts] = useState([]);
  const [storageCharts, setStorageCharts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen((prev) => !prev);
  const navigate = useNavigate();
  const { data, isLoading, error } = useContext(DataContext);
  const levelColors = {
    normal: Colors.alertsgreen100,
    poor: Colors.alertsred90,
    critical: Colors.alertsred100 ,
    warning: Colors.alertsyellow100,
    
  };

  useEffect(() => {
    const cpuCharts = cpuData.map((el) => {
      return {
        chart: generateCircularProgressBarOption(
          el.level === "no_data" ? "N/A" : el.value + "%",
          null,
          levelColors[el.level],
          el.level === "no_data" ? 100 : el.value
        ),
        ...el,
      };
    });
    setCpuCharts(cpuCharts);

    const cpuUserCharts = cpuUserData.map((el) => {
      return {
        chart: generateCircularProgressBarOption(
          el.level === "no_data" ? "N/A" : el.value + "%",
          null,
          levelColors[el.level],
          el.level === "no_data" ? 100 : el.value
        ),
        ...el,
      };
    });
    setCpuUserCharts(cpuUserCharts);

    const memoryCharts = memoryData.map((el) => {
      return {
        chart: generateCircularProgressBarOption(
          el.level === "no_data" ? "no data" : el.value + "%",
          null,
          levelColors[el.level],
          el.level === "no_data" ? 100 : el.value
        ),
        ...el,
      };
    });
    setMemoryCharts(memoryCharts);

    const storageCharts = storageData.map((el) => {
      return {
        chart: generateCircularProgressBarOption(
          el.level === "no_data" ? "no data" : el.value + "%",
          null,
          levelColors[el.level],
          el.level === "no_data" ? 100 : el.value
        ),
        ...el,
      };
    });
    setStorageCharts(storageCharts);
  }, [cpuData, cpuUserData, memoryData, storageData]);

  const { i18n, t } = useContext(DataContext);

  return (

    <div
      style={{
      }}
    >
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <div style={{ paddingLeft: "1rem" }}>
          <h2 style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
            {i18n.language === "id"
              ? "Device Capacity Belum Termonitor"
              : "Device Capacity Not Monitored Yet"}
          </h2>
          <ol style={{ width: "30rem", listStyleType: "decimal" }}>
            <li>
              {i18n.language === "id"
                ? "Pastikan Anda sudah melakukan ceklis untuk opsi SNMP ketika menambahkan perangkat"
                : "Make sure you have checked the SNMP option when adding the device"}
            </li>
            <li>
              {i18n.language === "id"
                ? "Aktifkan SNMP pada perangkat Anda untuk monitoring perangkat jaringan. Klik "
                : "Activate SNMP on your device for network device monitoring. Click "}
              <a
                style={{ all: "revert" }}
                href="https://netmonk.id/help/articles/%5Bgs-panduan-9%5Dbagaimana-cara-setup-snmp"
              >
                {i18n.language === "id" ? "link berikut" : "the following link"}
              </a>

              {i18n.language === "id"
                ? " untuk melakukan setup SNMP pada perangkat Anda"
                : " to set up SNMP on your device"}
              <img src={snmpOption} />
            </li>
            <li>
              {i18n.language === "id"
                ? "Jika Anda masih mengalami masalah, "
                : "If you are still experiencing problems, "}
              <a
                style={{ all: "revert" }}
                href="https://api.whatsapp.com/send/?phone=628111720237&text&type=phone_number&app_absent=0"
              >
                {i18n.language === "id" ? "hubungi kami" : "contact us"}
              </a>
              .
            </li>
          </ol>
        </div>
      </Modal>
      <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
        {t("prime.detailAnalysis.deviceCapacityandQoS.header")}
        {isLoading || error || data.device === null || (data?.device[0].count_component.normal === 0 && data?.device[0].count_component.warning === 0 && data?.device[0].count_component.critical === 0) ? null :
          ' ' + formatCountstoString([
            { count: data?.device[0].count_component.normal, status: "Normal" },
            { count: data?.device[0].count_component.warning, status: "Warning" },
            { count: data?.device[0].count_component.critical, status: "Critical" },
          ])
        }
      </div>

      {cpuData.length === 0 &&
        cpuUserData.length === 0 &&
        memoryData.length === 0 &&
        storageData.length === 0 ? (
        <div
          style={{
            width: "100%",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
            paddingTop: "0.3rem",
            fontSize: "0.8rem",
          }}
        >
          {i18n.language === "id"
            ? "Data tidak tersedia"
            : "Data not available"}{" "}
          <QuestionMarkCircleIcon
            width={"1rem"}
            style={{ display: "inline", cursor: "pointer" }}
            onClick={toggleModal}
          />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "0.5rem",
          }}
        >
          <div
            style={{
              minWidth: 0,
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: Colors.neutral10,
                borderTopLeftRadius: "5px",
                marginBottom: "5px",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
              }}
            >
              CPU
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                overflowX: "scroll",
                backgroundColor: Colors.neutral2,
                borderBottomLeftRadius: "5px",
                padding: "0.5rem",
                height: "6.5rem",
              }}
            >
              {/* Variable content inside child 1 */}
              {cpuCharts.map((el, idx) => (
                <div key={`cpu-${idx}`}>
                  <PieChart
                    options={el.chart}
                    chartWidth={60}
                    chartHeight={60}
                    onClick={() => navigate("component-detail/cpu_system/" + el.id)
                    }
                  />
                  <div style={{ textAlign: "center", fontSize: "0.8rem" }}>
                    CPU System

                  </div>
                </div>
              ))}
              {cpuUserCharts.map((el, idx) => (
                <div key={`cpu-user-${idx}`}>
                  <PieChart
                    options={el.chart}
                    chartWidth={60}
                    chartHeight={60}
                    onClick={() => navigate("component-detail/cpu_user/" + el.id)}
                  />
                  <div style={{ textAlign: "center", fontSize: "0.8rem" }}>
                    CPU User
                  </div>
                </div>
              ))}
            </div>
          </div>



          <div style={{ minWidth: 0 }}>
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: Colors.neutral10,
                marginBottom: "5px",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.memory")}
            </div>
            <div style={{ display: "flex", gap: "0.5rem", overflowX: "scroll",
              backgroundColor: Colors.neutral2, height: "6.5rem", padding: "0.5rem"
             }}>
              {memoryCharts.map((el, idx) => (
                <div key={`memory-${idx}`}>
                  <PieChart
                    options={el.chart}
                    chartWidth={60}
                    chartHeight={60}
                    onClick={() => navigate("component-detail/memory/" + el.id)}
                  />

                </div>
              ))}
            </div>
          </div>

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: Colors.neutral10,
                borderTopRightRadius: "5px",
                marginBottom: "5px",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "0.3rem",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.storage")}
            </div>
            <div style={{ display: "flex", gap: "0.5rem", overflowX: "scroll", borderBottomRightRadius: "5px", backgroundColor: Colors.neutral2, height: "6.5rem", padding: "0.5rem" }}>
              {storageCharts.map((el, idx) => (
                <div key={`storage-${idx}`}>
                  <PieChart
                    options={el.chart}
                    chartWidth={60}
                    chartHeight={60}
                    onClick={() => navigate("component-detail/storage/" + el.id)}
                  />

<div style={{ textAlign: "center", fontSize: "0.8rem" }}>
                    {idx}{" "}
                    <InformationCircleIcon
                      color="darkgrey"
                      width={"1rem"}
                      style={{
                        display: "inline",
                        cursor: "pointer",
                      }}
                      data-tooltip-id={`cpu-${idx}-tooltip`}
                    />
                    <Tooltip
                      id={`cpu-${idx}-tooltip`}
                      place="bottom"
                      style={{
                        fontSize: "0.7rem",
                        maxWidth: "200px",
                      }}
                      clickable
                    >
                      <div style={{ overflow: "auto" }}>{el.type}</div>
                    </Tooltip>
                  </div>

                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );


}


const ProcessServerCard = () => {
  const { data, isLoading, error, t, i18n, isMobile, formatCountstoString } = useContext(DataContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen((prev) => !prev);

  const columns = [
    {
      Header: t("prime.detailAnalysis.processServerTable.processName"),
      accessor: "process_name",
    },
    {
      Header: t("prime.detailAnalysis.processServerTable.totalCPU"),
      accessor: "cpu_usage",
    },
    {
      Header: t("prime.detailAnalysis.processServerTable.totalMemory"),
      accessor: "mem_usage"
    },
    {
      Header: t("prime.detailAnalysis.processServerTable.totalDiskIORead"),
      accessor: "diskio_read"
    },
    {
      Header: t("prime.detailAnalysis.processServerTable.totalDiskIOWrite"),
      accessor: "diskio_write"
    },
    {
      Header: t("prime.detailAnalysis.processServerTable.totalThread"),
      accessor: "thread_num"
    },
  ];

  return (
    <div
      style={{
        width: "100%",
        
      }}
    >
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <div style={{ paddingLeft: "1rem" }}>
          <h2 style={{ fontWeight: "bold", fontSize: "1.25rem", marginBottom: "1rem" }}>
            {i18n.language === "id"
              ? "Proses Server Belum Termonitor"
              : "Server Processes Not Monitored Yet"}
          </h2>
          <ol style={{ width: "30rem", listStyleType: "decimal" }}>
            <li style={{ marginBottom: "1rem" }}>
              {i18n.language === "id"
                ? "Akses menu Server Status pada modul Server Monitoring"
                : "Access the Server Status menu in the Server Monitoring module"}
              <img src={ctaProcesses1}
                style={{ width: "40%" }}
              />
            </li>
            <li style={{ marginBottom: "1rem" }}>
              {i18n.language === "id"
                ? "Klik Add New Server"
                : "Click Add New Server"}
              <img src={ctaProcesses2}
                style={{ width: "40%" }}
              />
            </li>
            <li style={{ marginBottom: "1rem" }}>
              {i18n.language === "id"
                ? "Pada opsi Processes, isilah nama proses yang ingin dimonitor pada server tertentu. Jika anda ingin memonitor semua proses dalam suatu server, kosongkan input field."
                : "In the Processes option, fill in the name of the process you want to monitor on a particular server. If you want to monitor all processes on a server, leave the input field blank."}
              <img src={ctaProcesses3}
                style={{ width: "50%" }}
              />
            </li>
          </ol>
        </div>
      </Modal>
      <div style={{ fontWeight: "bold", marginBottom: "10px" }}>

        {t("prime.detailAnalysis.processServerTable.header")} {isLoading || error || !data || data.device[0].process.length === 0 || data.device[0].count_process === 0
          ? null : `(${data.device[0].count_process})`}
      </div>
      <div style={{ overflowX: "auto", overflowY: "auto",
          maxHeight: "200px",
          width: "100%",
          }}>
      <table style={{ width: "100%" }}>
        <thead>
          <tr>
            {columns.map((column, idx) => (
              <th
                key={idx}
                style={{
                  backgroundColor: Colors.neutral10,
                  borderTopLeftRadius: idx === 0 ? "5px" : "0",
                  
                  borderTopRightRadius: idx === columns.length - 1 ? "5px" : "0",
                  
                  fontWeight: 600,
                  padding: "0.5rem 1rem",
                  fontSize: "0.8rem",
                }}
              >
                {column.Header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody
          style={{
            fontSize: "0.8rem",
            width: "100%",
            backgroundColor: Colors.neutral2,
          }}
        >
          {isLoading || error || !data || data.device[0].process.length === 0 ? (
            <tr style={{ textAlign: "center", height: "1.5rem", padding: "1rem", marginTop: "5px" }}>
              <td colSpan={columns.length} style={{ padding: "1rem", fontSize: "0.8rem" }}>


                {i18n.language === "id"
                  ? "Data tidak tersedia "
                  : "Data not available "}

                <QuestionMarkCircleIcon
                  width={"1rem"}
                  style={{ display: "inline", cursor: "pointer" }}
                  onClick={toggleModal}
                />
              </td>
            </tr>
          ) : (
            data.device[0].process.map((row, idx) => (
              <tr
                key={idx}
                style={{
                  textAlign: "center",
                  height: "1.5rem",
                  borderBottom: `1px solid ${Colors.neutral10}`,
                }}
              >
                {columns.map((column, idx) => (
                  <td key={idx}>{row[column.accessor] === "diskio_read" || row[column.accessor] === "diskio_write" ? row[column.accessor] * 0.000001 + "mb" : row[column.accessor]}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        
      </table>
      </div>
    </div>
  );
}

const ContainerServerCard = () => {
  const { data, isLoading, error, t, i18n, formatCountstoString } = useContext(DataContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen((prev) => !prev);
  const isMobile =  window.innerWidth <= 570;

  const columns = [
    {
      Header: t("prime.detailAnalysis.containerServerTable.containerName"),
      accessor: "container_name",
    },
    {
      Header: t("prime.detailAnalysis.containerServerTable.containerStatus"),
      accessor: "container_status",
    },
    {
      Header: t("prime.detailAnalysis.containerServerTable.uptime"),
      accessor: "uptime"
    },
    {
      Header: t("prime.detailAnalysis.containerServerTable.totalCPU"),
      accessor: "cpu_usage"
    },
    {
      Header: t("prime.detailAnalysis.containerServerTable.totalMemory"),
      accessor: "mem_usage"
    },
    {
      Header: t("prime.detailAnalysis.containerServerTable.limit"),
      accessor: "limit"
    },
    {
      Header: t("prime.detailAnalysis.containerServerTable.networkIO"),
      accessor: "network_io"
    },
    {
      Header: t("prime.detailAnalysis.containerServerTable.blockIO"),
      accessor: "block_io"
    },

  ];

  return (
    <div
      style={{
      }}
    >
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <div style={{ paddingLeft: "1rem" }}>
          <h2 style={{ fontWeight: "bold", fontSize: "1.25rem", marginBottom: "1rem" }}>
            {i18n.language === "id"
              ? "Container Server Belum Termonitor"
              : "Server Containers Not Monitored Yet"}
          </h2>
          <ol style={{ width: "30rem", listStyleType: "decimal" }}>
            <li style={{ marginBottom: "1rem" }}>
              {i18n.language === "id"
                ? "Akses menu Server Status pada modul Server Monitoring"
                : "Access the Server Status menu in the Server Monitoring module"}
              <img src={ctaProcesses1}
                style={{ width: "40%" }}
              />
            </li>
            <li style={{ marginBottom: "1rem" }}>
              {i18n.language === "id"
                ? "Klik Add New Server"
                : "Click Add New Server"}
              <img src={ctaProcesses2}
                style={{ width: "40%" }}
              />
            </li>
            <li style={{ marginBottom: "1rem" }}>
              {i18n.language === "id"
                ? "Pada opsi Container, isilah nama container yang ingin dimonitor pada server tertentu. Jika anda ingin memonitor semua container dalam suatu server, kosongkan input field."
                : "In the Container option, fill in the name of the container you want to monitor on a particular server. If you want to monitor all containers on a server, leave the input field blank."}
              <img src={ctaContainer}
                style={{ width: "50%" }}
              />
            </li>
          </ol>
        </div>
      </Modal>

      <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
        Container 
        
        {` `+ formatCountstoString([
          { status: "Running", count: data?.device[0]?.count_container?.running },
          { status: "Paused", count: data?.device[0]?.count_container?.paused },
          { status: "Stopped", count: data?.device[0]?.count_container?.exited }
        ])}


      </div>
      <div style={{ overflowX: "auto", overflowY: "auto",
          maxHeight: "300px",
          width: "100%",
          
          }}>
      <table style={{ width: "100%",
        
       }}>
        <thead>
          <tr>
            {columns.map((column, idx) => (
              <th
                key={idx}
                style={{
                  backgroundColor: Colors.neutral10,
                  borderTopLeftRadius: idx === 0 ? "5px" : "0",
                  borderTopRightRadius: idx === columns.length - 1 ? "5px" : "0",
                  fontWeight: 600,
                  padding: "0.5rem 1rem",
                  fontSize: "0.8rem",
                }}
              >
                {column.Header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody
          style={{
            
            fontSize: "0.8rem",
            backgroundColor: Colors.neutral2,
            
          }}
        >
          
          {isLoading || error || !data || data.device[0].container === undefined || data.device[0].container.length === 0 ? (
            <tr style={{ textAlign: "center", height: "1.5rem", padding: "1rem", marginTop: "5px" }}>
              <td colSpan={columns.length} style={{ padding: "1rem", fontSize: "0.8rem" }}>
                {i18n.language === "id"
                  ? "Data tidak tersedia "
                  : "Data not available "}
                <QuestionMarkCircleIcon
                  width={"1rem"}
                  style={{ display: "inline", cursor: "pointer" }}
                  onClick={toggleModal}
                />
              </td>
            </tr>
          ) : (
            data.device[0].container.map((row, idx) => (
              <tr
                key={idx}
                style={{
                  textAlign: "center",
                  height: "1.5rem",
                  borderBottom: `1px solid ${Colors.neutral10}`,
                }}
              >
                {columns.map((column, idx) => (
                  <td key={idx}>{
                    column.accessor === "network_io" ? row['network_in'] + " / " + row['network_out'] :
                      column.accessor === "block_io" ? row['block_in'] + " / " + row['block_out'] :
                        row[column.accessor]}</td>

                ))}
              </tr>
            ))
          )}
          
        </tbody>
        
        
      </table>

      </div>
    </div>
  );
}




const Picker = ({ isToday, toggleToday }) => {
  const { t } = useContext(DataContext);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "1rem",
        borderRadius: "0.5rem",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        borderBlock: "1px solid rgba(0,0,0,0.1)",
      }}
    >
      <button
        style={{
          padding: "0.5rem 0.5rem",
          backgroundColor: isToday ? Colors.primary100 : Colors.white,
          color: isToday ? Colors.white : Colors.primary100,
          border: "none",
          cursor: "pointer",
          borderTopLeftRadius: "0.5rem",
          borderBottomLeftRadius: "0.5rem",
          fontWeight: isToday ? "bold" : "normal",
          fontSize: "0.8rem",
        }}
        onClick={toggleToday}
      >
        {t("prime.detailAnalysis.deviceCapacityandQoS.today")}
      </button>
      <button
        style={{
          padding: "0.5rem 0.5rem",
          backgroundColor: !isToday ? Colors.primary100 : Colors.white,
          color: !isToday ? Colors.white : Colors.primary100,
          border: "none",
          cursor: "pointer",
          borderTopRightRadius: "0.5rem",
          borderBottomRightRadius: "0.5rem",
          fontWeight: !isToday ? "bold" : "normal",
          fontSize: "0.8rem",
        }}
        onClick={toggleToday}
      >
        {t("prime.detailAnalysis.deviceCapacityandQoS.thisWeek")}
      </button>
    </div>
  );
};

const PerformanceChart = ({ data, labels }) => {
  const chartRef = useRef(null); // Create a ref for the chart container

  useEffect(() => {
    if (chartRef.current) {
      // Initialize the chart
      const myChart = echarts.init(chartRef.current);

      // Set the chart options
      myChart.setOption({
        tooltip: {
          trigger: "axis",
        },
        xAxis: {
          type: "category",
          data: labels,
        },
        yAxis: {
          type: "value",
        },
        series: [
          {
            data: data,
            type: "line",
          },
        ],
      });

      // Cleanup on component unmount
      return () => {
        myChart.dispose();
      };
    }
  }, [data, labels]); // Re-initialize the chart when data or labels change

  return (
    <div
      ref={chartRef} // Assign the ref to the container
      style={{ width: "100%", height: 300 }}
    ></div>
  );
};

const TrendTableCard = ({ clickAnomaly, type }) => {
  const {
    dataToday,
    isLoadingToday,
    errorToday,
    dataWeek,
    isLoadingWeek,
    errorWeek,
    isToday,
    t,
    i18n,
  } = useContext(DataContext);

  return (
    <div
      style={{
        width: "100%",
        
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                backgroundColor: Colors.neutral10,
                padding: "0.5rem 1rem",
                marginBottom: "5px",
                fontSize: "0.8rem",
                fontWeight: 700,
                borderBottomLeftRadius: "5px",
                borderTopLeftRadius: "5px",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.trendPattern")}
            </th>
            <th
              style={{
                backgroundColor: Colors.neutral10,
                padding: "0.5rem 1rem",
                marginBottom: "5px",
                fontSize: "0.8rem",
                fontWeight: 700,
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.trendSlope")}
            </th>
            <th
              style={{
                backgroundColor: Colors.neutral10,
                padding: "0.5rem 1rem",
                marginBottom: "5px",
                fontSize: "0.8rem",
                fontWeight: 600,
                borderBottomRightRadius: "5px",
                borderTopRightRadius: "5px",
              }}
            >
              {t("prime.detailAnalysis.deviceCapacityandQoS.anomaly")}
            </th>
          </tr>
        </thead>
        <tbody>
          {isToday ? (
            isLoadingToday || !dataToday ? (
              <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                Loading...
              </p>
            ) : errorToday ? (
              <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                {errorToday.message}
              </p>
            )
              : dataToday.error ? (
                <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                  {dataToday.message}
                </p>
              ) : (

                <tr style={{ fontSize: "0.8rem" }}>

                  <td style={{ textAlign: "center" }}>
                    {dataToday.trend_pattern.pattern ? dataToday.trend_pattern.pattern : "No data"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {dataToday.trend_pattern.trend_slope ? `${dataToday.trend_pattern.trend_slope}%` : "No data"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {dataToday.is_anomaly === "no" ? (
                      "No"
                    ) : (
                      <div
                        style={{
                          color: "blue",
                          underline: "true",
                          cursor: "pointer",
                        }}
                        onClick={clickAnomaly}
                      >
                        {t("prime.detailAnalysis.deviceCapacityandQoS.view")}
                      </div>
                    )}
                  </td>
                </tr>
              )

          )
            : (
              isLoadingWeek || !dataWeek ? (
                <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                  Loading...
                </p>
              ) : errorWeek ? (
                <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                  {errorWeek.message}
                </p>
              )
                : dataWeek.error ? (
                  <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                    {dataWeek.message}
                  </p>
                ) : (
                  <tr style={{ fontSize: "0.8rem" }}>
                    <td style={{ textAlign: "center" }}>
                      {dataWeek.trend_pattern.pattern ? dataWeek.trend_pattern.pattern : "No data"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {dataWeek.trend_pattern.trend_slope ? `${dataWeek.trend_pattern.trend_slope}%` : "No data"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {dataWeek.is_anomaly === "no" ? (
                        "No"
                      ) : (
                        <div
                          style={{
                            color: "blue",
                            underline: "true",
                            cursor: "pointer",
                          }}
                          onClick={clickAnomaly}
                        >
                          {t("prime.detailAnalysis.deviceCapacityandQoS.view")}
                        </div>
                      )}
                    </td>
                  </tr>
                )
            )}


        </tbody>
      </table>
    </div>
  );
};

const SummaryAnalysisCard = ({ type }) => {
  const {
    dataToday,
    isLoadingToday,
    errorToday,
    dataWeek,
    isLoadingWeek,
    errorWeek,
    isToday,
    t,
    i18n,
  } = useContext(DataContext);
  return (
    <div style={{ }}>
      <p style={{ fontWeight: "bold", fontSize: "1.1rem", color: "black", marginBottom: "10px" }}>
        {t("prime.detailAnalysis.deviceCapacityandQoS.summaryAnalysis.header")}
      </p>
      <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
        {isToday ? (
          isLoadingToday || !dataToday ? (
            <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
              Loading...
            </p>
          ) : errorToday ? (
            <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
              {errorToday.message}
            </p>
          )
            : dataToday.error ? (
              <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
                {dataToday.message}
              </p>
            ) : i18n.language === "id" ? (
              <p>
                <strong>Kondisi Web/API</strong> Anda hari ini
                dalam kondisi {dataToday.usage_condition_category}.
                Rata-rata response time pada <strong>hari ini</strong> adalah{" "}
                {dataToday.mean_usage.toFixed(2)}{" "}
                s, nilai response time memiliki trend <strong>kenaikan</strong>{" "}
                sebesar{" "}
                <strong>
                  {dataToday.trend_pattern.trend_slope}% per hari
                </strong>{" "}
                dengan pola trend yaitu{" "}
                <strong>{dataToday.trend_pattern.pattern}</strong>,
                {dataToday.is_anomaly === "no"
                  ? " tidak ditemukan anomali pada data hari ini"
                  : " terdapat anomali pada data hari ini"}
                .
              </p>
            ) : (
              <p>
                The <strong>Web/API Condition</strong> today
                is {dataToday.usage_condition_category}.
                The average response time on <strong>today</strong> is{" "}
                {dataToday.mean_usage.toFixed(2)}{" "} s, response time has
                a <strong>increase</strong> trend of{" "}
                <strong>
                  {dataToday.trend_pattern.trend_slope}% per day
                </strong>{" "}
                with trend pattern{" "}
                <strong>{dataToday.trend_pattern.pattern}</strong>,
                {dataToday.is_anomaly === "no"
                  ? " no anomalies found in today's data"
                  : " anomalies found in today's data"}
                .

              </p>
            )
        ) : isLoadingWeek || !dataWeek ? (
          <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
            Loading...
          </p>
        ) : errorWeek ? (
          <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
            {errorWeek.message}
          </p>
        )
          : dataWeek.error ? (
            <p style={{ fontSize: "0.8rem", textAlign: "justify" }}>
              {dataWeek.message}
            </p>
          ) : i18n.language === "id" ? (
            <p>
              Kondisi <strong>Web/API</strong> Anda minggu ini
              dalam kondisi {dataWeek.usage_condition_category}.
              Rata-rata response time pada <strong>minggu ini</strong> adalah{" "}
              {dataWeek.mean_usage.toFixed(2)}{" "}
              s, response time memiliki trend <strong>kenaikan</strong>{" "}
              sebesar{" "} <strong>
                {dataWeek.trend_pattern.trend_slope}% per hari
              </strong>{" "}
              dengan pola trend yaitu{" "}
              <strong>{dataWeek.trend_pattern.pattern}</strong>,
              {dataWeek.is_anomaly === "no"
                ? " tidak ditemukan anomali pada data minggu ini"
                : " terdapat anomali pada data minggu ini"}
              .


            </p>
          ) : (
            <p>
              The <strong>Web/API Condition</strong> this week is{" "}
              {dataWeek.usage_condition_category}.
              The average response time on <strong>this week</strong> is{" "}
              {dataWeek.mean_usage.toFixed(2)}{" "}
              s, response time has a <strong>increase</strong> trend of{" "}
              <strong>
                {dataWeek.trend_pattern.trend_slope}% per day
              </strong>{" "}
              with trend pattern{" "}
              <strong>{dataWeek.trend_pattern.pattern}</strong>,
              {dataWeek.is_anomaly === "no"
                ? " no anomalies found in this week's data"
                : " anomalies found in this week's data"}
              .
            </p>
          )}
      </p>
    </div>
  );
};

const FloatingAnomalies = ({ type, closeAnomaly }) => {
  const {
    data,
    isLoading,
    error,
    dataWeek,
    isLoadingWeek,
    errorWeek,
    isToday,
    t,
    i18n,
  } = useContext(DataContext);

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 999,
          pointerEvents: "auto",
        }}
        onClick={closeAnomaly}
      ></div>

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          backgroundColor: "white",
          boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
          borderRadius: "10px",
          transform: "translate(-50%, -50%)",
          zIndex: 1000,
          cursor: "pointer",
          width: window.innerWidth > 600 ? "400px" : "80%",
        }}
      >
        <Card
          style={{
            padding: "1rem",
            maxHeight: window.innerHeight - 30,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                fontSize: "1.1rem",
                color: "black",
              }}
            >
              {i18n.language === "id" ? (
                <p>Daftar Anomali </p>
              ) : (
                <p>List of Anomalies</p>
              )}
            </div>
            <button
              onClick={closeAnomaly}
              style={{
                border: "none",
                backgroundColor: "white",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              X
            </button>
          </div>
          <table style={{ width: "100%" }}>
            <thead>
              <tr
                style={{
                  backgroundColor: Colors.neutral20,
                  borderRadius: "5px",
                }}
              >
                <th style={{ fontSize: "0.8rem", fontWeight: 700 }}>
                  {i18n.language === "id" ? "Waktu" : "Time"}
                </th>
                <th style={{ fontSize: "0.8rem", fontWeight: 700 }}>
                  {i18n.language === "id" ? "Nilai" : "Value"}
                </th>
                <th style={{ fontSize: "0.8rem", fontWeight: 700 }}>
                  {i18n.language === "id" ? "Deviasi" : "Deviation"}
                </th>
              </tr>
            </thead>
            <tbody>
              {isToday ? (
                isLoading ? (
                  <tr>
                    <td colSpan="3" style={{ fontSize: "0.8rem" }}>
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="3" style={{ fontSize: "0.8rem" }}>
                      Error: {error.message}
                    </td>
                  </tr>
                ) : (
                  data.metrics[type].anomalies.map((anomaly) => (
                    <tr key={anomaly.time}>
                      <td style={{ fontSize: "0.8rem" }}>
                        {anomaly.time.slice(0, 16)}
                      </td>
                      <td style={{ fontSize: "0.8rem" }}>
                        {anomaly[type].toFixed(2)}
                        {type === "packet_loss" ? " %" : " ms"}
                      </td>
                      <td style={{ fontSize: "0.8rem" }}>
                        {anomaly.deviation.toFixed(2)}
                      </td>
                    </tr>
                  ))
                )
              ) : isLoadingWeek || !dataWeek ? (
                <tr>
                  <td colSpan="3" style={{ fontSize: "0.8rem" }}>
                    Loading...
                  </td>
                </tr>
              ) : errorWeek ? (
                <tr>
                  <td colSpan="3" style={{ fontSize: "0.8rem" }}>
                    Error: {errorWeek.message}
                  </td>
                </tr>
              ) : (
                dataWeek.metrics[type].anomalies.map((anomaly) => (
                  <tr key={anomaly.time}>
                    <td style={{ fontSize: "0.8rem" }}>
                      {anomaly.time.slice(0, 16)}
                    </td>
                    <td style={{ fontSize: "0.8rem" }}>
                      {anomaly[type].toFixed(2)}
                      {type === "packet_loss" ? " %" : " ms"}
                    </td>
                    <td style={{ fontSize: "0.8rem" }}>
                      {anomaly.deviation.toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
};


